import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFileUpload } from '../../hooks/useFileUpload';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../ui/LoadingSpinner';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiDownload, FiFile, FiUpload } = FiIcons;

const ResourceManager = ({ resources = [], onChange }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newResource, setNewResource] = useState({ label: '', description: '', file_url: '' });
  const { uploadFile, uploading } = useFileUpload();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const path = `resources/${Date.now()}-${file.name}`;
      const uploadedFile = await uploadFile(file, path);
      const fileUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/course-resources/${uploadedFile.path}`;
      
      setNewResource(prev => ({
        ...prev,
        file_url: fileUrl,
        label: prev.label || file.name
      }));
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleAddResource = () => {
    if (!newResource.label || !newResource.file_url) return;

    const updatedResources = [...resources, { ...newResource, id: Date.now() }];
    onChange(updatedResources);
    
    setNewResource({ label: '', description: '', file_url: '' });
    setShowAddForm(false);
  };

  const handleRemoveResource = (index) => {
    const updatedResources = resources.filter((_, i) => i !== index);
    onChange(updatedResources);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Downloadable Resources</h4>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="text-nutrition-600 hover:text-nutrition-700 font-medium flex items-center space-x-1"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Resource</span>
        </button>
      </div>

      {/* Add Resource Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resource Title *
                </label>
                <input
                  type="text"
                  value={newResource.label}
                  onChange={(e) => setNewResource(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  placeholder="e.g., Course Workbook PDF"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newResource.description}
                  onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  placeholder="Brief description of the resource"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Upload *
                </label>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-2 pb-3">
                      <SafeIcon icon={FiUpload} className="w-6 h-6 mb-2 text-gray-500" />
                      <p className="mb-1 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span>
                      </p>
                      <p className="text-xs text-gray-500">PDF, DOC, XLSX, etc.</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {uploading && (
                  <div className="flex items-center justify-center mt-2">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-600">Uploading...</span>
                  </div>
                )}
                {newResource.file_url && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    ✓ File uploaded successfully
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewResource({ label: '', description: '', file_url: '' });
                  }}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddResource}
                  disabled={!newResource.label || !newResource.file_url || uploading}
                  className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  Add Resource
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resources List */}
      {resources.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiFile} className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No resources added yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {resources.map((resource, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiDownload} className="w-5 h-5 text-nutrition-600" />
                <div>
                  <p className="font-medium text-gray-900">{resource.label}</p>
                  {resource.description && (
                    <p className="text-sm text-gray-500">{resource.description}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <a
                  href={resource.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-nutrition-600 hover:text-nutrition-700 text-sm font-medium"
                >
                  Preview
                </a>
                <button
                  type="button"
                  onClick={() => handleRemoveResource(index)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceManager;